import {
    FC,
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from 'react'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'

interface ContextValue {
    setCloseHandler: (id: string, closeHandler: () => void) => void
    clearCloseHandler: (id: string) => void
}

const Context = createContext<ContextValue>({
    setCloseHandler: () => {},
    clearCloseHandler: () => {},
})

interface DismissOverlayProviderProps {
    children: ReactNode
    open?: boolean
}

const DismissOverlayProvider: FC<DismissOverlayProviderProps> = ({ children, open }) => {
    const closeHandlersRef = useRef<Record<string, () => void>>({})

    const clearCloseHandler = useCallback((id: string) => {
        delete closeHandlersRef.current[id]
    }, [])

    const close = useCallback(() => {
        const id = Object.keys(closeHandlersRef.current)[0]

        if (!id) return

        closeHandlersRef.current[id]()

        clearCloseHandler(id)
    }, [clearCloseHandler])

    const setCloseHandler = useCallback(
        (id: string, closeHandler: () => void) => {
            close()

            closeHandlersRef.current[id] = closeHandler
        },
        [close]
    )

    const value = useMemo(
        () => ({ setCloseHandler, clearCloseHandler }),
        [setCloseHandler, clearCloseHandler]
    )

    useEffect(() => {
        if (open === false) close()
    }, [open, close])

    const tap = Gesture.Tap().runOnJS(true).onStart(close)

    return (
        <Context.Provider value={value}>
            <GestureDetector gesture={tap}>{children}</GestureDetector>
        </Context.Provider>
    )
}

export default DismissOverlayProvider

export const useDismissOverlayProviderContext = () => useContext(Context)
